/**
 * Tracks viewed products in localStorage and renders a "Recently Viewed" carousel.
 *
 * Runs entirely client-side (products are fetched from the Storefront JSON endpoint
 * after page load), so the price/sign-in logic here mirrors — but can't directly reuse —
 * snippets/price-gate.liquid. Keep the two in sync if that snippet's behavior changes.
 */
class RecentlyViewedProducts extends HTMLElement {
  static STORAGE_KEY = 'gsm_recently_viewed';
  static MAX_STORED = 20;

  connectedCallback() {
    this.trackCurrentProduct();
    this.render();
  }

  getStoredHandles() {
    try {
      const raw = window.localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY);
      const handles = raw ? JSON.parse(raw) : [];
      return Array.isArray(handles) ? handles : [];
    } catch (error) {
      return [];
    }
  }

  setStoredHandles(handles) {
    try {
      window.localStorage.setItem(RecentlyViewedProducts.STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      // localStorage unavailable (private browsing, storage full, etc.) — fail silently
    }
  }

  trackCurrentProduct() {
    const currentHandle = this.dataset.productHandle;
    if (!currentHandle) return;

    const handles = this.getStoredHandles().filter((handle) => handle !== currentHandle);
    handles.unshift(currentHandle);
    this.setStoredHandles(handles.slice(0, RecentlyViewedProducts.MAX_STORED));
  }

  async render() {
    const currentHandle = this.dataset.productHandle;
    const limit = parseInt(this.dataset.limit, 10) || 6;
    const handles = this.getStoredHandles()
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);

    if (handles.length === 0) return;

    const products = (await Promise.all(handles.map((handle) => this.fetchProduct(handle)))).filter(Boolean);
    if (products.length === 0) return;

    const list = this.querySelector('ul');
    if (!list) return;

    const loggedIn = this.dataset.customerLoggedIn === 'true';
    const signInUrl = this.dataset.signInUrl;
    list.innerHTML = products.map((product) => this.renderCard(product, loggedIn, signInUrl)).join('');
    this.removeAttribute('hidden');
  }

  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  renderCard(product, loggedIn, signInUrl) {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageMarkup = image
      ? `<img src="${this.imageUrl(image, 533)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="533" height="533" class="motion-reduce">`
      : '';
    const priceMarkup = loggedIn
      ? `<span class="price-item price-item--regular">${this.formatMoney(product.price)}</span>`
      : `<a href="${signInUrl}?return_url=${encodeURIComponent(window.location.pathname)}" class="price-gate__link">Sign in for price</a>`;

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard card--media">
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent">
                  <a href="${product.url}" class="full-unstyled-link">${imageMarkup}</a>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${product.url}" class="full-unstyled-link">${this.escapeHtml(product.title)}</a>
                </h3>
                <div class="card-information">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">${priceMarkup}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  imageUrl(src, width) {
    return src.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, (match, ext, query) => `_${width}x${ext}${query || ''}`);
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
